import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Lock, ShieldCheck, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState({ min_length: 8 });

  useEffect(() => {
    api.get("/auth/password-policy").then(r => setPolicy(r.data || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing or invalid reset token in URL");
    if (np !== np2) return toast.error("Passwords don't match");
    if (np.length < (policy.min_length || 8)) return toast.error(`Password must be at least ${policy.min_length || 8} characters`);
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: np });
      toast.success("Password updated — please sign in with your new password.");
      setTimeout(() => nav("/login", { replace: true }), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Request a new link.");
    } finally { setBusy(false); }
  };

  const rules = [
    { pass: np.length >= (policy.min_length || 8), label: `At least ${policy.min_length || 8} characters` },
    ...(policy.require_upper ? [{ pass: /[A-Z]/.test(np), label: "One uppercase letter" }] : []),
    ...(policy.require_lower ? [{ pass: /[a-z]/.test(np), label: "One lowercase letter" }] : []),
    ...(policy.require_number ? [{ pass: /\d/.test(np), label: "One number" }] : []),
    ...(policy.require_symbol ? [{ pass: /[^A-Za-z0-9]/.test(np), label: "One symbol" }] : []),
  ];

  return (
    <div className="min-h-screen grid md:grid-cols-2 dark bg-background text-foreground">
      <Toaster theme="dark" richColors />
      <div className="hidden md:block relative overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 30%, rgba(37,99,235,0.25), transparent 45%), radial-gradient(circle at 80% 80%, rgba(249,115,22,0.2), transparent 50%)" }} />
        <div className="relative z-10 p-10 lg:p-14 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-10 w-auto" />
            <div>
              <div className="font-heading font-bold text-lg text-logo-gradient">Zenith 360 ERP</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/60">Password reset</div>
            </div>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
              Set a<br /><span className="text-logo-gradient">new password</span>
            </h1>
            <div className="text-sm text-white/60 mt-4 max-w-xs">One-time link — resets your account and signs you out of every other device.</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> bcrypt hashed · nothing stored plain-text
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="reset-form">
          <div className="space-y-1.5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent">// Password reset</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Choose a new password</h2>
            <div className="text-sm text-foreground/70">
              Your link is single-use and valid for 60 minutes from when it was issued.
            </div>
          </div>
          {!token && (
            <div className="text-xs text-red-500 border border-red-500/40 bg-red-500/10 p-3 rounded-sm">
              No reset token in the URL. <Link to="/forgot-password" className="underline">Request a fresh link →</Link>
            </div>
          )}
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">New password</Label>
            <div className="relative mt-1.5">
              <Input type={show ? "text" : "password"} required minLength={policy.min_length || 8}
                value={np} onChange={(e) => setNp(e.target.value)}
                className="rounded-sm pr-10" data-testid="reset-np" autoFocus />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Confirm new password</Label>
            <Input type={show ? "text" : "password"} required minLength={policy.min_length || 8}
              value={np2} onChange={(e) => setNp2(e.target.value)}
              className="rounded-sm mt-1.5" data-testid="reset-np2" />
          </div>
          {rules.length > 0 && (
            <ul className="text-[11px] space-y-1" data-testid="reset-rules">
              {rules.map((r, i) => (
                <li key={i} className={r.pass ? "text-emerald-500" : "text-muted-foreground"}>
                  {r.pass ? "✓" : "•"} {r.label}
                </li>
              ))}
            </ul>
          )}
          <Button type="submit" disabled={busy || !token}
            className="w-full rounded-sm bg-logo-gradient hover:opacity-95 text-white h-11"
            data-testid="reset-submit">
            <Lock className="w-4 h-4 mr-2" /> {busy ? "Updating…" : "Set new password"}
          </Button>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-foreground/60 hover:text-foreground">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}
